import React from "react";
import { useTranslation } from "react-i18next";
import cookieConsentService from "../../services/cookieConsent";
import type { CookieConsentProvider } from "../../services/cookieConsent";

interface CookiePreferencesProps {
  /** Consent provider to use; defaults to the configured service. */
  provider?: CookieConsentProvider;
  /** Extra CSS class names appended to the wrapper. */
  className?: string;
}

/**
 * Control that reopens the consent preference centre.
 *
 * The provider only renders its banner while no decision has been recorded, and
 * Termly hides its own floating preferences button wherever the visitor's region
 * is configured for implied consent, so without this control a returning guest
 * has no way to withdraw or change consent from the checkout.
 *
 * It reads no consent state, so it deliberately does not use
 * `useCookieConsent`: that hook owns the banner and the subscription, and this
 * control only needs to ask for the preference centre.
 */
const CookiePreferences: React.FC<CookiePreferencesProps> = ({ provider = cookieConsentService, className = "" }) => {
  const { t } = useTranslation();

  if (!provider.canShowPreferences()) return null;

  return (
    <div className={`gcw:flex gcw:justify-center gcw:py-4 ${className}`.trim()}>
      <button
        type="button"
        data-testid="cookie-preferences"
        className="gcw:text-sm gcw:text-gray-700 gcw:underline gcw:underline-offset-2 gcw:hover:text-gray-900 gcw:focus-visible:outline gcw:focus-visible:outline-2 gcw:focus-visible:outline-offset-2"
        onClick={() => provider.showPreferences()}
      >
        {t("cookie_preferences", "Cookie preferences")}
      </button>
    </div>
  );
};

export default CookiePreferences;
